import { useState } from "react";
import { AxiosError } from "axios";

import { useToast, Button, Center, Heading, Image, Input, InputGroup, InputLeftElement, VStack } from "@chakra-ui/react";
import { AtSignIcon, LockIcon } from "@chakra-ui/icons";
import { postLogin } from "../utils/api";
import firmProLogo from "../assets/firmpro_logo.svg";
import { useNavigate } from "react-router";


/**
 * La pantalla para iniciar sesión.
 * 
 * @param {LoginParams}
 * @returns {JSX.Element}
 */
export default function LoginScreen(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  /**
   * Al enviar el formulario, extrae las credenciales ingresadas en el
   * formulario y las envía al backend para validarlas.
   * 
   * Si todo sale bien, el backend responde con un tóken JWT para guardarlo en
   * localStorage y mostrar la página principal. De lo contrario, responde con
   * un mensaje de error que se mostrará en un toast. 
   * 
   * @param {React.FormEvent} event El evento de envío de formulario que
   * gatilla a esta función.
   */
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoggingIn(true);

    const { email, password } = document.forms[0];
    
    postLogin(email.value, password.value)
      .then(_ => navigate("/"))
      .catch((err: AxiosError) => {
        // Se muestra un toast con el error plasmado en errorMessage.
        let errorMessage = "";
        if (err.response) {
          // Si el backend respondió con un error (código no 2xx):
          errorMessage = err.response.data as string;
        } else if (err.request) {
          // Si hubo petición, pero no respuesta del servidor:
          errorMessage = "Error de conexión con el servidor."
        } else {
          // Si algo más salió mal:
          errorMessage = err.message;
        }
        toast({
          title: "Error",
          description: errorMessage,
          status: "error",
          duration: 9000,
          isClosable: true,
        })
      })
      .finally(() => setIsLoggingIn(false));
  };

  return (
    <Center height={window.innerHeight} bg="pink.800">
      <form method="post" onSubmit={handleSubmit}>
        <VStack borderRadius="md" p={{"base": "8", "md": "12"}} bg="white" spacing="6" shadow="xl">
          <Image src={firmProLogo} width={{"base": "250px", "md": "300px"}} />
          <Heading>Semáforo FirmPro</Heading>
          <Heading as="h3">Iniciar sesión</Heading>

          {/* Campo de nombre de usuario */}
          <InputGroup>
            <InputLeftElement pointerEvents="none" children={<AtSignIcon/>} />
            <Input isRequired name="email" placeholder="Correo" borderColor="pink.800"/>
          </InputGroup>
          
          {/* Campo de contraseña */}
          <InputGroup>
            <InputLeftElement pointerEvents="none" children={<LockIcon/>} />
            <Input isRequired name="password" type="password" placeholder="Contraseña" borderColor="gray.500"/>
          </InputGroup>

          <Button isLoading={isLoggingIn} type="submit" colorScheme="pink" shadow="lg">Ingresar</Button>
        </VStack>
      </form>
    </Center>
  );
};
